import { ChevronDown, ChevronUp, Headset, Mic, MicOff, Pause, Phone, PhoneOff, Play } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import { IconButton } from "@/shared/ui/icon-button";
import { StatusBadge } from "@/shared/ui/status-badge";
import { TimerDisplay } from "@/shared/ui/timer";
import { callerLabel } from "./callerInfo";
import { MIC_PROBLEM_TEXT, useMicGuard, useMicGuardStore } from "./micGuard";
import { answer, hangup, reject, toggleHold, toggleMute } from "./sipEngine";
import { useSoftphoneStore } from "./softphoneStore";
import { callStateMeta, REG_META, SESSION_META } from "./softphoneUi";

const COLLAPSE_KEY = "pm.softphone.mini.collapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Mini softphone persistente (AppShell): o agente navega pelo sistema sem perder a chamada.
 * Fica oculto em `/atendimento` (a página já tem os controles completos) e para quem não tem ramal.
 * Também monta a guarda do microfone (`useMicGuard`), que precisa rodar em qualquer tela.
 */
export function MiniSoftphone() {
  useMicGuard();
  const { pathname } = useLocation();
  const phone = useSoftphoneStore((s) => s.phone);
  const ownership = useSoftphoneStore((s) => s.ownership);
  const call = useSoftphoneStore((s) => s.call);
  const reg = useSoftphoneStore((s) => s.reg);
  const sessionStatus = useSoftphoneStore((s) => s.sessionStatus);
  const sessionInfo = useSoftphoneStore((s) => s.sessionInfo);
  const micProblem = useMicGuardStore((s) => s.problem);
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);

  if (!phone || ownership !== "this" || pathname.startsWith("/atendimento")) return null;
  if (!call && sessionStatus === "OFFLINE" && reg === "idle" && !micProblem) return null;

  const ringing = call?.state === "ringing";
  // Tocando sempre abre: atender não pode depender de expandir.
  const collapsed = collapsedPref && !ringing;
  const setCollapsed = (v: boolean) => {
    setCollapsedPref(v);
    try {
      localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  const sessionMeta = SESSION_META[sessionStatus];
  const regMeta = REG_META[reg];
  const statusLabel = sessionStatus === "PAUSED" && sessionInfo ? `Em pausa: ${sessionInfo}` : sessionMeta.label;

  return (
    <aside
      aria-label="Softphone"
      data-testid="mini-softphone"
      data-collapsed={collapsed || undefined}
      className={cn(
        "fixed right-3 top-[4.25rem] z-[120] rounded-xl border bg-surface-raised shadow-md animate-pop-in sm:right-4",
        collapsed ? "w-auto" : "w-[min(calc(100vw-1.5rem),20rem)]",
        ringing ? "border-status-ringing text-status-ringing motion-safe:animate-pulse-ring" : "border-border",
      )}
    >
      <div className="flex items-center gap-2 p-2 pl-3 text-fg">
        {call ? (
          <StatusBadge meta={callStateMeta(call)} />
        ) : (
          <StatusBadge meta={sessionMeta} label={collapsed ? sessionMeta.label : statusLabel} className="min-w-0" />
        )}
        {collapsed ? (
          call ? <TimerDisplay since={call.answeredAt ?? call.createdAt} label="Duração" /> : null
        ) : (
          <span className="tnum min-w-0 flex-1 truncate text-caption text-fg-subtle" title={regMeta.label}>
            Ramal {phone.phoneNumber}
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {!collapsed ? (
            <Link
              to="/atendimento"
              aria-label="Abrir atendimento"
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-caption font-medium text-brand-accent hover:bg-brand-subtle"
            >
              <Headset className="h-3.5 w-3.5" aria-hidden /> Abrir
            </Link>
          ) : null}
          {!ringing ? (
            <IconButton
              icon={collapsed ? ChevronDown : ChevronUp}
              label={collapsed ? "Expandir softphone" : "Recolher softphone"}
              size="sm"
              tooltipSide="left"
              onClick={() => setCollapsed(!collapsed)}
            />
          ) : null}
        </div>
      </div>

      {!collapsed && micProblem ? (
        <div className="mx-2 mb-2 rounded-md bg-danger-subtle px-3 py-2 text-caption text-danger-fg" role="alert">
          <p className="flex items-center gap-1.5 font-semibold">
            <MicOff className="h-3.5 w-3.5 shrink-0" aria-hidden /> Sem microfone — você não receberá chamadas
          </p>
          <p className="mt-0.5">
            {MIC_PROBLEM_TEXT[micProblem].title}.{" "}
            <Link to="/atendimento" className="font-semibold underline underline-offset-2">
              Resolver
            </Link>
          </p>
        </div>
      ) : null}

      {!collapsed && call ? (
        <div className="space-y-3 border-t border-border p-3 text-fg" aria-live="assertive">
          <div className="flex min-w-0 items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-body font-semibold">
              {ringing ? "Chamada de " : ""}
              {callerLabel(call.remote)}
            </p>
            <TimerDisplay since={call.answeredAt ?? call.createdAt} size="md" label="Duração" />
          </div>
          {ringing ? (
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <button
                type="button"
                onClick={answer}
                aria-label="Atender"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-success text-body font-medium text-on-success transition-colors hover:bg-success/90"
              >
                <Phone className="h-4 w-4" aria-hidden /> Atender
              </button>
              <IconButton icon={PhoneOff} label="Recusar" variant="danger" size="lg" onClick={reject} />
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <IconButton
                icon={call.muted ? MicOff : Mic}
                label={call.muted ? "Ativar microfone" : "Mudo"}
                variant="subtle"
                pressed={call.muted}
                disabled={call.state !== "active"}
                onClick={toggleMute}
              />
              <IconButton
                icon={call.held ? Play : Pause}
                label={call.held ? "Retomar" : "Espera"}
                variant="subtle"
                pressed={call.held}
                disabled={call.state !== "active" || !!call.consult}
                onClick={toggleHold}
              />
              <button
                type="button"
                onClick={hangup}
                aria-label={call.state === "dialing" ? "Cancelar" : "Desligar"}
                className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-danger px-3 text-body-sm font-medium text-on-danger transition-colors hover:bg-danger-hover"
              >
                <PhoneOff className="h-4 w-4" aria-hidden /> {call.state === "dialing" ? "Cancelar" : "Desligar"}
              </button>
            </div>
          )}
        </div>
      ) : null}
    </aside>
  );
}
